import React, { useEffect, useState } from "react";
import FastAPIClient from "../../client";
import config from "../../config";

import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import { HiArrowDownTray, HiExclamationTriangle } from "react-icons/hi2";

const client = new FastAPIClient(config);

// Define types for the document data
interface DocumentsProps {
    clientId: string;
}

interface DocumentStatus {
    document_status_id: string;
    document_id: string;
    transmission_date: string | null;
    signature_date: string | null;
    document: {
        name: string;
        document_link: string;
    };
}

const Documents: React.FC<DocumentsProps> = ({ clientId }) => {
    const [documents, setDocuments] = useState<DocumentStatus[]>([]);
    const [originalDocuments, setOriginalDocuments] = useState<
        DocumentStatus[]
    >([]); // To store the original data
    const [isModified, setIsModified] = useState<boolean>(false); // Track if there are changes
    const [message, setMessage] = useState<{
        type: "success" | "error" | "";
        text: string;
    }>({ type: "", text: "" });

    const [isLoading, setIsLoading] = useState(true);

    // Function to trigger download
    const handleDownload = (url: string) => {
        window.open(url, "_blank");
    };

    useEffect(() => {
        getUser(clientId);
    }, [clientId]);

    const getUser = async (clientId: string) => {
        try {
            const response = await client.getDocuments(clientId);
            setDocuments(response);
            setOriginalDocuments(response); // Store the original data for comparison
        } catch (error) {
            console.error("Error fetching notes:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLSelectElement>,
        field: string
    ) => {
        const value =
            e.target.value === "Oui" ? new Date().toISOString() : null;

        const updatedDocuments = documents.map((doc) =>
            doc.document_id === e.target.name ? { ...doc, [field]: value } : doc
        );

        setDocuments(updatedDocuments);

        // Check if any document is modified
        const isAnyModified = !updatedDocuments.every((doc, index) => {
            return (
                doc.transmission_date ===
                    originalDocuments[index].transmission_date &&
                doc.signature_date === originalDocuments[index].signature_date
            );
        });

        setIsModified(isAnyModified);
    };

    const saveChanges = () => {
        console.log("documents", documents);

        // Create an array of promises for updating each document separately
        const updatePromises = documents.map((document) =>
            client.updateDocuments(document)
        );

        // Execute all promises sequentially or concurrently
        Promise.all(updatePromises)
            .then(() => {
                setOriginalDocuments(documents);
                setIsModified(false);
                setMessage({
                    type: "success",
                    text: "Tous les documents ont été mis à jour avec succès !",
                });
            })
            .catch(() => {
                setMessage({
                    type: "error",
                    text: "Échec de la mise à jour de certains ou de tous les documents. Veuillez réessayer.",
                });
            });
    };

    const handleCancel = () => {
        // Reset the documents to their original state
        setDocuments(originalDocuments);
        setIsModified(false); // Reset the modification flag
    };

    return (
        <div className="pt-10 w-full">
            {message.text && (
                <div
                    className={`fixed top-4 left-1/2 transform -translate-x-1/2 p-4 rounded-xl bg-opacity-80 text-white shadow-lg ${
                        message.type === "success"
                            ? "bg-green-500"
                            : "bg-rose-500"
                    }`}
                >
                    {message.text}
                </div>
            )}

            <div className="w-full font-lg font-bold text-left mb-5 ml-4">
                À transmettre
            </div>
            {isLoading ? (
                <div className="mt-4">
                    <Box>
                        {[...Array(4)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rounded"
                                height={40}
                                className="mb-4"
                            />
                        ))}
                    </Box>
                </div>
            ) : documents.length === 0 ? (
                <div className=" text-black mt-5 text-left border border-gray-200 rounded-md p-4 flex flex-row items-center">
                    <HiExclamationTriangle />
                    <div className="ml-4">Fonctionnalité à venir !</div>
                </div>
            ) : (
                <>
                    <table className="table-auto w-full border-collapse text-sm">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left"></th>
                                <th className="px-4 py-2 text-left">
                                    Transmis
                                </th>
                                <th className="px-4 py-2 text-left">Signée</th>
                                <th className="px-4 py-2 text-left"></th>
                            </tr>
                        </thead>
                        <tbody className="">
                            {documents.map((document) => (
                                <tr
                                    key={document.document_id}
                                    className="border-b"
                                >
                                    <td className="px-4 py-2 text-left font-semibold">
                                        {document.document.name}
                                    </td>
                                    <td className="px-4 py-2">
                                        {document.transmission_date ? (
                                            // Display the green "Oui" button only if the document is saved
                                            <div className="flex flex-row items-center justify-center">
                                                <button className="flex bg-green-500 bg-opacity-50 text-green-500 py-1 px-3 rounded-full">
                                                    Oui
                                                </button>
                                                <div
                                                    className="flex"
                                                    title={`Transmission Date: ${document.transmission_date}`}
                                                >
                                                    <div className="bg-gray-400 text-white rounded-full w-4 h-5 ml-1 font-bold">
                                                        i
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <select
                                                className="border rounded-md p-2"
                                                value={
                                                    document.transmission_date
                                                        ? "Oui"
                                                        : "Non"
                                                }
                                                name={document.document_id}
                                                onChange={(e) =>
                                                    handleChange(
                                                        e,
                                                        "transmission_date"
                                                    )
                                                }
                                            >
                                                <option value="Non">Non</option>
                                                <option value="Oui">Oui</option>
                                            </select>
                                        )}
                                    </td>
                                    <td className="px-4 py-2">
                                        {document.signature_date ? (
                                            // Display the green "Oui" button only if the document is saved
                                            <div className="flex flex-row items-center justify-center">
                                                <button className="flex bg-green-500 bg-opacity-50 text-green-500 py-1 px-3 rounded-full">
                                                    Oui
                                                </button>
                                                <div
                                                    className="flex"
                                                    title={`Signature Date: ${document.signature_date}`}
                                                >
                                                    <div className="bg-gray-400 text-white rounded-full w-4 h-5 ml-1 font-bold">
                                                        i
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <select
                                                className="border rounded-md p-2"
                                                value={
                                                    document.signature_date
                                                        ? "Oui"
                                                        : "Non"
                                                }
                                                name={document.document_id}
                                                onChange={(e) =>
                                                    handleChange(
                                                        e,
                                                        "signature_date"
                                                    )
                                                }
                                            >
                                                <option value="Non">Non</option>
                                                <option value="Oui">Oui</option>
                                            </select>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        <button
                                            onClick={() =>
                                                handleDownload(
                                                    document.document
                                                        .document_link
                                                )
                                            }
                                            className="bg-gray-200 text-black px-2 py-1 rounded-full text-sm font-bold"
                                        >
                                            <span className="hidden md:block">
                                                Télécharger
                                            </span>
                                            <HiArrowDownTray className="w-5 h-5 md:hidden" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {isModified && (
                <div className="absolute bottom-0 mb-10 left-1/2 transform -translate-x-1/2 flex flex-row items-center justify-center p-2 bg-gray-200 md:w-1/2 rounded-xl">
                    <div className="text-sm font-semibold mr-4">
                        Voulez-vous enregistrer les modifications ?
                    </div>
                    <div className="flex space-x-4">
                        <button
                            className="p-2 bg-gray-400 text-white text-sm rounded-full z-50"
                            onClick={handleCancel} // Cancel button
                        >
                            Annuler
                        </button>
                        <button
                            className="p-2 bg-gold text-black font-bold text-sm rounded-full z-50"
                            onClick={saveChanges} // Save button
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export function DocumentsList({ clientId }: DocumentsProps) {
    return <Documents clientId={clientId} />;
}
